import React, { useEffect, useRef, useState } from "react";

// Thumb colour is the app's primary blue (#4253C7, hover #3547B8). Classes are
// written out literally below so Tailwind generates them.

/**
 * Slim scrollbar that stays invisible until the pointer hovers over its strip,
 * then shows a blue thumb that can be dragged (or the track clicked) to
 * scroll `targetRef`. The target keeps its native scrollbar hidden.
 * Renders nothing when the content fits.
 *
 * orientation="horizontal": a strip placed in the normal flow (put it where
 *   the bar should appear, e.g. above a board of columns).
 * orientation="vertical": an overlay strip on the right edge — render it
 *   inside a `relative` wrapper around the scrolling element.
 *
 * `watch` is any value whose change may alter the content size (e.g. the
 * list being rendered), so the thumb is re-measured.
 */
const HoverScrollbar = ({
  targetRef,
  watch,
  orientation = "horizontal",
  className = "",
  // Vertical only: where the strip sits horizontally. Override (e.g. with a
  // negative right offset) to move it into a card's padding, clear of icons.
  edgeClassName = "right-0",
}) => {
  const vertical = orientation === "vertical";
  const [metrics, setMetrics] = useState({ ratio: 1, offset: 0 });
  const trackRef = useRef(null);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return undefined;

    const update = () => {
      const size = vertical ? el.scrollHeight : el.scrollWidth;
      const view = vertical ? el.clientHeight : el.clientWidth;
      const pos = vertical ? el.scrollTop : el.scrollLeft;
      const ratio = size > 0 ? view / size : 1;
      const max = size - view;
      setMetrics({
        ratio,
        offset: max > 0 ? (pos / max) * (1 - ratio) : 0,
      });
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (observer) {
      observer.observe(el);
      Array.from(el.children).forEach((child) => observer.observe(child));
    }
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (observer) observer.disconnect();
    };
  }, [targetRef, watch, vertical]);

  if (metrics.ratio >= 0.999) return null;

  // Drag the thumb: map pointer movement along the track to content scroll.
  const startDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = targetRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const start = vertical ? e.clientY : e.clientX;
    const startScroll = vertical ? el.scrollTop : el.scrollLeft;
    const scale = vertical
      ? el.scrollHeight / track.clientHeight
      : el.scrollWidth / track.clientWidth;

    const onMove = (ev) => {
      const delta = ((vertical ? ev.clientY : ev.clientX) - start) * scale;
      if (vertical) el.scrollTop = startScroll + delta;
      else el.scrollLeft = startScroll + delta;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Click on the empty track: centre the view on that point.
  const jumpTo = (e) => {
    e.stopPropagation();
    const el = targetRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    if (vertical) {
      const fraction = (e.clientY - rect.top) / rect.height;
      el.scrollTo({
        top: fraction * el.scrollHeight - el.clientHeight / 2,
        behavior: "smooth",
      });
    } else {
      const fraction = (e.clientX - rect.left) / rect.width;
      el.scrollTo({
        left: fraction * el.scrollWidth - el.clientWidth / 2,
        behavior: "smooth",
      });
    }
  };

  const thumbStyle = vertical
    ? { height: `${metrics.ratio * 100}%`, top: `${metrics.offset * 100}%` }
    : { width: `${metrics.ratio * 100}%`, left: `${metrics.offset * 100}%` };

  return (
    <div
      className={`group ${
        vertical ? `absolute ${edgeClassName} top-0 bottom-0 z-20 px-1` : "py-1.5"
      } ${className}`}
      aria-hidden="true"
    >
      <div
        ref={trackRef}
        onMouseDown={jumpTo}
        className={`relative rounded-full bg-transparent transition-colors duration-200 group-hover:bg-[#E0E4F7] cursor-pointer ${
          vertical ? "h-full w-2" : "h-2 w-full"
        }`}
      >
        <div
          onMouseDown={startDrag}
          className={`absolute rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100 bg-[#4253C7] hover:bg-[#3547B8] cursor-grab active:cursor-grabbing ${
            vertical ? "left-0 w-2" : "top-0 h-2"
          }`}
          style={thumbStyle}
        />
      </div>
    </div>
  );
};

export default HoverScrollbar;
